import { Point } from '@arcgis/core/geometry.js';
import { locationToAddress } from '@arcgis/core/rest/locator';
import * as Sentry from '@sentry/react';
import {
  AssetConfig,
  AssetType,
  AssetTypeConfig,
  assetTypeId,
  CemeteryAssetFeatureAttributes,
  CycleHangarsEstatesFeatureAttributes,
  CycleHangarsFeatureAttributes,
  HighwaysCarriagewaysFeatureAttributes,
  NearestApiResponse,
  ParkAndPlayspaceAssetFeatureAttributes,
  TreeAssetFeatureAttributes,
  PublicLightingElectricVehicleChargers,
  GulliesAsset,
} from '@types';
import axios from 'axios';
import * as ELG from 'esri-leaflet';
import L, {
  Icon,
  LatLngTuple,
  LayerGroup,
  LeafletMouseEvent,
  Map,
} from 'leaflet';
import proj4 from 'proj4';
import {
  WCC_ARCGIS_MAP_SERVER_BASE_URL,
  GEOCODE_SERVER,
  LOCATION_ENDPOINT,
  apiKey,
  NEAREST_ENDPOINT,
  NEAREST_ASSET_ENDPOINT,
  WITHIN_BOUNDARY_ENDPOINT,
  MAP_RADIUS,
} from './Map/constants';
import { createAssetIcon } from './Map/icons';
import { boroughBoundaryLayer } from './Map/layers';
import {
  tflBoundaryPopup,
  loadingPopup,
  errorZoomInPopup,
  outsideBoroughPopup,
  addressErrorPopup,
  parksErrorPopup,
} from './Map/mapPopups';
import {
  containsAddressFilterItem,
  isLatLngWithinBoundary,
  isMapCorrectZoomLevel,
  isWithinTfLBoundary,
} from './Map/validations';
import extendedBoundaryGeojson from './Map/WCC_EXTENDED_BOUNDARY.json';
import { ParksAndTreesAssetType } from '@parks/hooks/useParksStore';

export const queryFeatureLayerById = async (
  assetLayerType: AssetType,
  assetLabel: string,
  assetId: string,
) => {
  const queryParams = {
    where: `${assetLabel}='${assetId}'`,
    timeRelation: 'esriTimeRelationOverlaps',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    distance: '4',
    units: 'esriSRUnit_Foot',
    outFields: '*',
    returnGeometry: 'true',
    returnTrueCurves: 'false',
    returnIdsOnly: 'false',
    returnCountOnly: 'false',
    returnZ: 'false',
    returnM: 'false',
    returnDistinctValues: 'false',
    sqlFormat: 'none',
    featureEncoding: 'esriDefault',
    f: 'geojson',
  };

  const queryString = Object.entries(queryParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const queryUrl = `${WCC_ARCGIS_MAP_SERVER_BASE_URL}/${assetTypeId[assetLayerType]}/query?${queryString}`;

  try {
    const response = await axios.get(queryUrl);
    return response;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

// Helper to extract lat/lng from LatLngExpression
function getLatLng(latlng: L.LatLngExpression): { lat: number; lng: number } {
  if (Array.isArray(latlng)) {
    return { lat: latlng[0], lng: latlng[1] };
  } else if ('lat' in latlng && 'lng' in latlng) {
    return { lat: (latlng as L.LatLng).lat, lng: (latlng as L.LatLng).lng };
  } else if ('lat' in latlng && 'lon' in latlng) {
    return { lat: (latlng as any).lat, lng: (latlng as any).lon };
  }
  throw new Error('Invalid LatLngExpression');
}

export const createAssetFeatureLayer = (assetConfig: AssetTypeConfig) => {
  const assetType =
    typeof assetConfig === 'string' ? assetConfig : assetConfig.asset;
  const feature = `${WCC_ARCGIS_MAP_SERVER_BASE_URL}${assetTypeId[assetType]}`;

  return ELG.featureLayer({
    url: feature,
    simplifyFactor: 0.5,
    precision: 5,
    style: (feature) => {
      if (typeof assetConfig === 'string') {
        return { color: '#5897fcad', weight: 2 };
      }
      return {
        color: assetConfig.color || '#5897fcad',
        weight: assetConfig.weight || 2,
        opacity: assetConfig.opacity || 1,
        fillColor: assetConfig.fillColor || assetConfig.color || '#5897fcad',
        fillOpacity:
          assetConfig.fillOpacity !== undefined
            ? assetConfig.fillOpacity
            : assetConfig.opacity || 1,
        stroke: true,
        strokeColor: assetConfig.strokeColor,
      };
    },
    pointToLayer: (geojson, latlng) => {
      if (typeof assetConfig === 'string') {
        const marker = L.marker(latlng, { icon: createAssetIcon() });
        marker.on('add', function () {
          const el = marker.getElement();
          if (el) {
            const { lat, lng } = getLatLng(latlng);
            el.setAttribute('data-lat', String(lat));
            el.setAttribute('data-lng', String(lng));
          }
        });
        return marker;
      }

      // If a custom Icon object is provided, use it directly
      if (assetConfig.icon instanceof Icon) {
        const marker = L.marker(latlng, { icon: assetConfig.icon });
        marker.on('add', function () {
          const el = marker.getElement();
          if (el) {
            const { lat, lng } = getLatLng(latlng);
            el.setAttribute('data-lat', String(lat));
            el.setAttribute('data-lng', String(lng));
          }
        });
        return marker;
      }
      // Otherwise use our custom icon configuration
      const marker: L.Marker<any> = L.marker(latlng, {
        icon: createAssetIcon({
          shape: assetConfig.icon?.shape,
          size: assetConfig.icon?.size,
          fillColor: assetConfig.color,
          strokeColor: assetConfig.strokeColor,
          opacity: assetConfig.opacity,
        }),
      });
      marker.on('add', function () {
        const el = marker.getElement();
        if (el) {
          const { lat, lng } = getLatLng(latlng);
          el.setAttribute('data-lat', String(lat));
          el.setAttribute('data-lng', String(lng));
        }
        if (assetConfig && assetConfig.onAdd) {
          assetConfig.onAdd(geojson, marker);
        }
      });
      return marker;
    },
  });
};

export const dropMarkerOnMap = (
  event: LeafletMouseEvent,
  address: addressResponseType,
  mapMethods: MapMethods,
) => {
  const { addMarker, clearGraphics, map } = mapMethods;
  clearGraphics();
  const { lat, lng } = event.latlng;
  const popupContent = `<div padding: 10px; border-radius: 5px;">
            <span>${address.full_address}</span></div>`;
  map.flyTo([lat, lng]);
  addMarker([lat, lng]);
};

export function shimmer() {
  // create shimmer container
  const container = document.createElement('div');
  const child = document.createElement('div');
  container.appendChild(child);
  // create stylesheet
  const styleEl = document.createElement('style');
  document.head.appendChild(styleEl);
  const styleSheet = styleEl.sheet as CSSStyleSheet;
  const keyFrames = `@keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }`;
  styleSheet.insertRule(keyFrames, styleSheet.cssRules.length);
  container.style.cssText = `
    width: 100%;
    height: 38px;
    border-radius:5px;
    background-color: #ddd;
    position:relative;
    overflow:hidden;
  `;
  child.style.cssText = `
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, #ddd 0%, #f5f5f5 50%, #ddd 100%);
    background-repeat: no-repeat;
    background-size: 200% 100%;
    animation: loading 1.0s infinite;
    position:absolute;
  `;
  return container;
}

export const getAddressFromMapPoint = async (
  point: LatLngTuple,
): Promise<__esri.AddressCandidate | null> => {
  let address = null;
  const coords = new Point({ latitude: point[0], longitude: point[1] });
  await locationToAddress(
    GEOCODE_SERVER,
    {
      location: coords,
      // @ts-expect-error "wkid" is the only required property in the spatialReference object to output the correct coordinates
      outSpatialReference: { wkid: 4326 },
    },
    500,
  )
    .then((response) => {
      address = response;
    })
    .catch((e) => {
      address = null;
    });
  return address;
};

export type addressResponseType = {
  address_line_1: string;
  address_line_2: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
  latitude: string;
  longitude: string;
  full_address: string;
  asset?:
    | HighwaysCarriagewaysFeatureAttributes
    | CycleHangarsEstatesFeatureAttributes
    | CycleHangarsFeatureAttributes
    | CemeteryAssetFeatureAttributes
    | TreeAssetFeatureAttributes
    | ParkAndPlayspaceAssetFeatureAttributes
    | PublicLightingElectricVehicleChargers
    | GulliesAsset;
  easting?: string;
  northing?: string;
  is_commercial?: boolean;
  is_within_borough?: boolean;
};

interface GetResultsHandler {
  suggestTerm: string;
  sourceIndex: number;
}

export const handleGetSuggestions = async (
  params: GetResultsHandler,
  custodianCodes: CUSTODIAN_CODES[] = [CUSTODIAN_CODES.CITY_OF_WESTMINSTER],
) => {
  const requestUrl = `${LOCATION_ENDPOINT}?query=${encodeURIComponent(
    params.suggestTerm,
  )}&fq=${generateCustodianCodeList(custodianCodes)}&custom_sort=S`;
  return await axios
    .get(requestUrl, { headers: { 'Ocp-Apim-Subscription-Key': apiKey } })
    .then((response) => {
      if (
        response.data.results === undefined ||
        response.data.results.length === 0
      ) {
        console.warn('No suggestions found');
        return [];
      }
      return response.data.results
        .filter((suggestion: addressResponseType) => suggestion !== null)
        .filter(
          (suggestion: addressResponseType) =>
            !containsAddressFilterItem(suggestion.address_line_1),
        )
        .map((suggestion: addressResponseType) =>
          removeStreetRecordLabelFromResults(suggestion),
        );
    })
    .catch((error) => {
      Sentry.captureException(error);
      return [];
    });
};

export enum CUSTODIAN_CODES {
  CITY_OF_WESTMINSTER = '5990',
  CAMDEN = '5210',
  BRENT = '5150',
  KENSINGTON_AND_CHELSEA = '5600',
  CITY_OF_LONDON = '5030',
  LAMBETH = '5660',
  WANDSWORTH = '5960',
  ISLINGTON = '5570',
  SOUTHWARK = '5840',
  HAMMERSMITH_AND_FULHAM = '5390',
  BARNET = '5090', // added this for cemetery
  EALING = '5270', // add this for cemetery
}

/**
 * Generate a list of custodian codes separated by a space, ie: LOCAL_CUSTODIAN_CODE:5990 LOCAL_CUSTODIAN_CODE:5210
 * @param custodianCodeArray
 * @returns list of custodian codes
 */
export const generateCustodianCodeList = (
  custodianCodeArray: CUSTODIAN_CODES[],
) => {
  return custodianCodeArray
    .map((code) => `LOCAL_CUSTODIAN_CODE:${code}`)
    .join(' ');
};

export const getNearestAddress = async (
  latitude: number,
  longitude: number,
  custodianCodes: CUSTODIAN_CODES[] = [CUSTODIAN_CODES.CITY_OF_WESTMINSTER],
): Promise<addressResponseType | null> => {
  try {
    const requestUrl = `${NEAREST_ENDPOINT}?point=${encodeURIComponent(
      `${latitude},${longitude}`,
    )}&fq=${generateCustodianCodeList(custodianCodes)}&radius=${MAP_RADIUS}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    if (response.data.results && response.data.results.length > 0) {
      // Projections to get eastings and northings
      const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';
      const osgb36 =
        '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +datum=OSGB36 +units=m +no_defs';
      const [easting, northing] = proj4(wgs84, osgb36, [longitude, latitude]);

      //APIM doesn't accept floats, only the first half is required
      const eastingString = easting.toString().split('.')[0];
      const northingString = northing.toString().split('.')[0];
      const latitudeString = latitude.toString();
      const longitudeString = longitude.toString();

      const result = removeStreetRecordLabelFromResults(
        response.data.results[0],
      );
      return {
        ...result,
        latitude: latitudeString,
        longitude: longitudeString,
        easting: eastingString,
        northing: northingString,
      };
    } else {
      return null;
    }
  } catch (error) {
    Sentry.captureException(error);
    throw new Error();
  }
};

export const getNearestAsset = async (
  latitude: number,
  longitude: number,
  assetType: AssetType,
  tflAssetParam?: boolean,
  searchOutsideWccBoundary?: boolean,
): Promise<NearestApiResponse | null> => {
  try {
    const requestUrl = `${NEAREST_ASSET_ENDPOINT}?latitude=${latitude}&longitude=${longitude}&layer=${assetType}&includeTFLAsset=${!!tflAssetParam}&searchOutsideWccBoundary=${!!searchOutsideWccBoundary}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getWithinBoundaryCheck = async (
  latitude: number,
  longitude: number,
) => {
  try {
    const requestUrl = `${WITHIN_BOUNDARY_ENDPOINT}?latitude=${latitude}&longitude=${longitude}`;
    const response = await axios.get(requestUrl, {
      headers: { 'Ocp-Apim-Subscription-Key': apiKey },
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export interface MapMethods {
  clearGraphics: () => void;
  addMarker: (latlng: LatLngTuple, popupContent?: string | undefined) => void;
  onChange: (location: addressResponseType) => void;
  map: Map;
  assetType?: AssetTypeConfig[] | AssetTypeConfig | undefined;
  graphicsLayer: React.MutableRefObject<LayerGroup<any> | null>;
}

export interface MapMethodProps {
  mapMethods: MapMethods;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  assetType?: AssetTypeConfig[] | undefined;
  enableBuffer?: boolean;
  enableTfLBoundaryCheck?: boolean;
}

/**
 * Check for externally-managed public spaces (for eg. Royal Parks) and display error popup
 */
export const checkExternalPublicSpacesAndDisplayPopup = async (
  address: addressResponseType | undefined,
  map?: Map,
  event?: LeafletMouseEvent['latlng'],
) => {
  if (!address) return;
  if (event && map) {
    loadingPopup({ latlng: event } as LeafletMouseEvent, map);
  }
  try {
    const asset = await getNearestAsset(
      Number.parseFloat(address.latitude),
      Number.parseFloat(address.longitude),
      'ExternalPublicSpaces',
      false, // tfl asset param
      false, // search outside of WccBoundary
    );
    if (asset?.features && asset.features.length > 0) {
      const attributes = asset.features
        .filter((feature) => feature.attributes)
        .map((feature) => feature.attributes)[0] as
        | ParkAndPlayspaceAssetFeatureAttributes
        | undefined;
      if (attributes) {
        const owner = attributes.Owner;
        if (event && map) {
          parksErrorPopup(event, map, owner);
        }
        return true;
      }
    }
  } catch (error) {
    if (event && map) {
      parksErrorPopup(event, map, undefined);
    }
    return;
  }
  map && map.closePopup();
};

export const addPublicSpacesAndCemeteriesAsset = async (
  assetType: AssetType,
  onChange: (location: addressResponseType) => void,
  address: addressResponseType,
  map?: Map,
  event?: LeafletMouseEvent['latlng'],
) => {
  let owner: number | undefined;
  let attributes:
    | ParkAndPlayspaceAssetFeatureAttributes
    | CemeteryAssetFeatureAttributes
    | undefined;
  try {
    const asset = await getNearestAsset(
      Number.parseFloat(address.latitude),
      Number.parseFloat(address.longitude),
      assetType,
      false, // tfl asset param
      assetType === 'Cemeteries' || assetType === 'Trees', // search outside of WccBoundary
    );
    if (asset?.features && asset.features.length > 0) {
      attributes = asset.features
        .filter((feature) => feature.attributes)
        .map((feature) => feature.attributes)[0] as
        | ParkAndPlayspaceAssetFeatureAttributes
        | CemeteryAssetFeatureAttributes
        | undefined;
      if (attributes) {
        onChange({
          ...address,
          asset: attributes,
        });
        // to close mapPopup before function returns
        map && map.closePopup();
        return true;
      }
    }
    // if no asset found or asset.features is empty
    onChange({
      ...address,
      asset: undefined,
    });
    owner = (attributes as ParkAndPlayspaceAssetFeatureAttributes).Owner;
    if (event && map && assetType === 'PublicSpaces') {
      parksErrorPopup(event, map, owner);
    } else {
      map && map.closePopup();
    }
    return;
  } catch (error) {
    onChange({
      ...address,
      asset: undefined,
    });
    // check if the asset is Royal park or other park owners
    if (assetType === 'PublicSpaces' || assetType === 'Trees') {
      const isExternalPark = await checkExternalPublicSpacesAndDisplayPopup(
        address,
        map,
        event,
      );
      if (!isExternalPark) {
        if (event && map && assetType === 'PublicSpaces') {
          parksErrorPopup(event, map, undefined);
        }
        return;
      }
    }
  }
};

export const addAsset = async (
  assetType: AssetTypeConfig | AssetTypeConfig[] | undefined,
  onChange: (location: addressResponseType) => void,
  address: addressResponseType,
  feature?: {
    properties:
      | HighwaysCarriagewaysFeatureAttributes
      | ParksAndTreesAssetType
      | CycleHangarsEstatesFeatureAttributes
      | CycleHangarsFeatureAttributes;
  } | null,
  map?: Map,
  event?: LeafletMouseEvent['latlng'],
) => {
  let assetAttributes:
    | HighwaysCarriagewaysFeatureAttributes
    | ParksAndTreesAssetType
    | CycleHangarsEstatesFeatureAttributes
    | CycleHangarsFeatureAttributes
    | undefined;
  let assetString: AssetType | undefined;
  if (event && map) {
    loadingPopup({ latlng: event } as LeafletMouseEvent, map);
  }
  if (feature?.properties) {
    // Use the feature properties directly if provided
    assetAttributes = feature.properties;
  } else if (assetType !== undefined) {
    // Fall back to API call if no feature provided
    assetString =
      typeof (assetType as AssetType)[0] === 'string'
        ? (assetType as AssetType)
        : (assetType as AssetConfig[])[0].asset;

    const containsAssetsOutsideOfBoundary =
      typeof (assetType as AssetType)[0] === 'string'
        ? false
        : (assetType as AssetConfig[])[0].containsAssetsOutsideOfBoundary ||
          false;

    if (
      assetString === 'PublicSpaces' ||
      assetString === 'Cemeteries' ||
      assetString === 'Trees'
    ) {
      await addPublicSpacesAndCemeteriesAsset(
        assetString,
        onChange,
        address,
        map,
        event,
      );
      return;
    }

    const asset = await getNearestAsset(
      Number.parseFloat(address.latitude),
      Number.parseFloat(address.longitude),
      assetString as AssetType,
      false,
      containsAssetsOutsideOfBoundary,
    );
    if (asset?.features && asset.features.length > 0) {
      const attributes = asset.features
        .filter((feature) => feature.attributes)
        .map((feature) => feature.attributes);
      assetAttributes = attributes[0];
    }
  }
  if (assetAttributes && assetType) {
    onChange({
      ...address,
      asset: assetAttributes,
    });
    // to close mapPopup before function returns
    map && map.closePopup();
    return true;
  } else {
    onChange({
      ...address,
      asset: undefined,
    });
  }
  // to close mapPopup before function returns
  map && map.closePopup();
};

export const getCoordinates = (enableBuffer?: boolean) => {
  if (enableBuffer) {
    return extendedBoundaryGeojson.features[0].geometry.coordinates;
  }
  let coordinates: number[][][] | undefined = undefined;
  boroughBoundaryLayer.eachFeature((feat) => {
    if ('feature' in feat) {
      coordinates = feat['feature'].geometry.coordinates as number[][][];
    }
  });
  return coordinates;
};

export const checkTfLBoundaryAndDisplayPopup = async (
  assetType: AssetTypeConfig | undefined,
  address: addressResponseType,
  event: LeafletMouseEvent,
  map: Map,
) => {
  if (assetType) {
    const isInsideTfLBoundary = await isWithinTfLBoundary(address, assetType);
    if (isInsideTfLBoundary) {
      tflBoundaryPopup(event, map);
      return;
    }
  }
};

export const getCustodianCodes = (
  assetTypes: AssetTypeConfig | AssetTypeConfig[] | undefined,
  enableBuffer?: boolean,
) => {
  if (enableBuffer) {
    return Object.values(CUSTODIAN_CODES);
  } else if (assetTypes) {
    // check for cemeteries asset type
    if (Array.isArray(assetTypes)) {
      const custodianCodes = [CUSTODIAN_CODES.CITY_OF_WESTMINSTER];
      assetTypes.forEach((assetType) => {
        if (typeof assetType === 'string' && assetType === 'Cemeteries') {
          custodianCodes.push(CUSTODIAN_CODES.BARNET, CUSTODIAN_CODES.EALING);
        } else if (typeof assetType === 'object' && 'asset' in assetType) {
          if (assetType.asset === 'Cemeteries') {
            custodianCodes.push(CUSTODIAN_CODES.BARNET, CUSTODIAN_CODES.EALING);
          }
        }
      });
      return custodianCodes;
    }
  }
  return undefined;
};

export const onMapClick = async (
  event: LeafletMouseEvent,
  mapMethods: MapMethods,
  setSearchTerm?: (searchTerm: string) => void,
  enableBuffer?: boolean,
  enableTfLBoundaryCheck?: boolean,
) => {
  const { map, onChange, assetType, clearGraphics } = mapMethods;
  const { lat, lng } = event.latlng;
  setSearchTerm && setSearchTerm('');
  clearGraphics();
  loadingPopup(event, map);
  const correctZoomLevel = isMapCorrectZoomLevel(map);
  if (!correctZoomLevel) {
    errorZoomInPopup(event, map);
    return;
  }
  try {
    if (
      !isLatLngWithinBoundary(event.latlng.lat, event.latlng.lng, enableBuffer)
    ) {
      outsideBoroughPopup(event, map);
      return;
    }
    let addressFromPin = await getNearestAddress(
      lat,
      lng,
      getCustodianCodes(assetType, enableBuffer),
    );

    if (addressFromPin === null) {
      addressErrorPopup(map, event);
      return;
    }

    if (enableTfLBoundaryCheck) {
      // Check if the asset is within TfL boundary
      const withinTfLBoundary = await isWithinTfLBoundary(
        addressFromPin,
        assetType || 'HighwaysCarriageways',
      );

      if (withinTfLBoundary) {
        onChange({
          ...addressFromPin,
          asset: undefined,
        });
        tflBoundaryPopup(event, map);
        return;
      }
    }
    map.closePopup();
    await addAsset(
      assetType,
      onChange,
      addressFromPin,
      undefined,
      map,
      event.latlng,
    );
    setSearchTerm && setSearchTerm(addressFromPin.full_address);
    dropMarkerOnMap(event, addressFromPin, mapMethods);
  } catch (error) {
    Sentry.captureException(error);
    addressErrorPopup(map, event);
  }
};

export const removeStreetRecordLabelFromResults = (
  address: addressResponseType,
) => {
  if (address.address_line_1 === 'STREET RECORD') {
    address.full_address = address.full_address.replace('STREET RECORD, ', '');
  }

  return address;
};
